/**
 * Step 02 graphic: a client message, a typing indicator, and the work landing.
 * Ported from ChatGraphic.dc.html. Decorative -- hidden from assistive tech.
 */
const typingDot = (delay: string) => ({
  width: 5,
  height: 5,
  borderRadius: 999,
  background: 'var(--bw-ink)',
  animation: `hiw-dot .9s ease-in-out infinite ${delay}`,
});

export function ChatGraphic() {
  return (
    <div
      aria-hidden="true"
      className="flex h-full w-full items-center justify-center p-2.5"
    >
      <div
        style={{
          width: '100%',
          maxWidth: 392,
          border: '2px solid var(--bw-ink)',
          borderRadius: 16,
          background: '#fff',
          boxShadow: '4px 4px 0 var(--bw-ink)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderBottom: '2px solid var(--bw-ink)',
            background: 'var(--surface-sunken)',
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              border: '2px solid var(--bw-ink)',
              background: 'var(--bw-yellow)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: '800 9px/1 var(--font-display)',
              color: 'var(--bw-ink)',
            }}
          >
            B
          </span>
          <span
            style={{
              font: '700 11px/1 var(--font-body)',
              color: 'var(--bw-ink)',
            }}
          >
            Butter Websites
          </span>
          <span
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              font: '600 8px/1 var(--font-body)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: 'var(--status-success)',
              }}
            />
            online
          </span>
        </div>

        <div
          style={{
            padding: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
            background: '#fff',
          }}
        >
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '82%',
              border: '2px solid var(--bw-ink)',
              borderRadius: '14px 14px 14px 4px',
              background: 'var(--ink-050)',
              padding: '8px 11px',
              font: '400 11.5px/1.45 var(--font-body)',
              color: 'var(--bw-ink)',
              animation: 'hiw-in .38s var(--ease-bounce) both .1s',
            }}
          >
            Hey! Can you add fall flavors to all of our cakes and mention this
            on the homepage?
          </div>

          {/* typing bubble fades out as the reply arrives underneath it */}
          <div
            style={{
              width: '100%',
              position: 'relative',
              minHeight: 32,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                display: 'flex',
                gap: 4,
                alignItems: 'center',
                border: '2px solid var(--bw-ink)',
                borderRadius: 14,
                padding: '9px 11px',
                background: '#fff',
                animation:
                  'hiw-in .3s var(--ease-bounce) both 1s, hiw-fade .3s linear both 2.3s',
              }}
            >
              <span style={typingDot('0s')} />
              <span style={typingDot('.15s')} />
              <span style={typingDot('.3s')} />
            </div>
            <div
              style={{
                width: 'fit-content',
                maxWidth: '70%',
                border: '2px solid var(--bw-ink)',
                borderRadius: '14px 14px 4px 14px',
                background: 'var(--bw-yellow)',
                padding: '8px 11px',
                font: '600 11.5px/1.45 var(--font-body)',
                color: 'var(--bw-ink)',
                animation: 'hiw-in .38s var(--ease-bounce) both 2.4s',
              }}
            >
              Sure thing!
            </div>
          </div>

          {/* The wrapper's top padding is the room the tapback hangs in, so it
              can straddle the bubble's edge without shifting the thread. */}
          <div
            style={{
              alignSelf: 'flex-end',
              maxWidth: '80%',
              position: 'relative',
              paddingTop: 10,
            }}
          >
            <div
              style={{
                border: '2px solid var(--bw-ink)',
                borderRadius: '14px 14px 4px 14px',
                background: 'var(--bw-yellow)',
                padding: '8px 11px',
                font: '400 11.5px/1.45 var(--font-body)',
                color: 'var(--bw-ink)',
                animation: 'hiw-in .38s var(--ease-bounce) both 3.1s',
              }}
            >
              Fall collection is live and the homepage banner is up. Have a
              look.
            </div>

            {/* Tapback, placed like iOS Messages: straddling the bubble's top
                left corner, arriving after the delivery receipt.

                The design system says never emoji. This is the one place the
                rule is worth breaking -- the whole graphic is a pastiche of a
                messaging app, and a drawn heart would read as an icon in a UI
                where every reaction is a real glyph. */}
            <span
              style={{
                position: 'absolute',
                left: -8,
                top: 0,
                /* Fixed square, so the radius renders a circle rather than the
                   pill that padding around a single glyph produces. */
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid var(--bw-ink)',
                borderRadius: 999,
                background: '#fff',
                font: '400 10px/1 var(--font-body)',
                animation: 'hiw-in .3s var(--ease-bounce) both 3.9s',
              }}
            >
              ❤️
            </span>
          </div>

          <div
            style={{
              alignSelf: 'flex-end',
              font: '600 8px/1 var(--font-body)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              animation: 'hiw-in .3s ease both 3.6s',
            }}
          >
            Delivered · 11 min
          </div>
        </div>
      </div>
    </div>
  );
}
